import { supabase } from '@/lib/supabase';

const BUCKET = 'food-photos';
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB (aligné avec file_size_limit du bucket)
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Upload une photo de food item. Retourne le chemin (à stocker dans
 * food_items.photo_path). Génère un nom unique pour éviter les collisions.
 */
export async function uploadFoodPhoto(file: File): Promise<string> {
  if (file.size > MAX_BYTES) {
    throw new Error(
      `Fichier trop gros (${Math.round(file.size / 1024)} KB). Max ${MAX_BYTES / 1024 / 1024} MB.`,
    );
  }
  if (!ALLOWED_MIME.includes(file.type)) {
    throw new Error('Format non supporté. Utilise JPEG, PNG ou WEBP.');
  }

  const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg';
  const safeExt = ['jpg', 'jpeg', 'png', 'webp'].includes(ext) ? ext : 'jpg';
  const path = `${crypto.randomUUID()}.${safeExt}`;

  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type,
  });
  if (error) throw error;
  return path;
}

export interface PhotoTransform {
  width?: number;
  height?: number;
  /** Qualité 0-100 (défaut 75). */
  quality?: number;
}

/**
 * URL publique permanente d'une photo food. Pas de round-trip — synchrone.
 * Accepte un paramètre `transform` pour servir une version resize + compressée.
 */
export function getFoodPhotoPublicUrl(
  photoPath: string | null | undefined,
  transform?: PhotoTransform,
): string | null {
  if (!photoPath) return null;
  const options = transform
    ? {
        transform: {
          width: transform.width,
          height: transform.height,
          quality: transform.quality ?? 75,
          resize: 'cover' as const,
        },
      }
    : undefined;
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(photoPath, options);
  return data?.publicUrl ?? null;
}

/**
 * Supprime une photo du bucket. Tolérant aux échecs.
 */
export async function deleteFoodPhoto(
  photoPath: string | null | undefined,
): Promise<void> {
  if (!photoPath) return;
  const { error } = await supabase.storage.from(BUCKET).remove([photoPath]);
  if (error) {
    console.warn('Échec suppression photo food :', error);
  }
}
